import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { prefersReducedMotion } from "./prefersReducedMotion";

type Props = {
  className: string;
  innerClassName: string;
  scrollingUp: string;
  scrollingDown: string;
  pinned: string;
  scrollOffset?: number;
  throttle?: number;
  onMouseLeave?: any;
}

class StickyHeader extends React.Component<Props, never> {
  private headerRef: any;

  constructor(props: Props) {
    super(props);
    this.headerRef = React.createRef();
  }

  componentDidMount() {
    const {
      innerClassName,
      scrollingUp,
      scrollingDown,
      pinned,
      scrollOffset,
      throttle,

    } = this.props;

    const travelDistance = scrollOffset ? (scrollOffset - 1) : 24;
    const throttleDuration = throttle ? throttle : 50;
    let prevScrollPosition = 0;    

    const header = this.headerRef as HTMLElement;
    const headerInner = header.querySelector(`.${innerClassName}`) as HTMLElement;

    let headerHeight = 0;

    const supportPageOffset = window.pageXOffset !== undefined;
    const isCSS1Compat = (document.compatMode || '') === 'CSS1Compat';

    _initHeader();

    // only continue if user does not have prefers reduced motion set
    if (prefersReducedMotion()) {  
      return;
    }

    _observeHeader();

    function _initHeader() {
      if (!header || !headerInner) {
        return false;
      }

      const resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header--height', 'auto');
        headerHeight = headerInner.offsetHeight;
        document.documentElement.style.setProperty('--header--height', headerHeight + 'px');
      });

      resizeObserver.observe(headerInner);
    }

    function _observeHeader() {
      function handler(entries:any) {
        const scrollPosition = _getScrollPosition();
        if (!entries[0].isIntersecting && scrollPosition > 0) {
          header.classList.add(pinned);
        }
      }

      const observer = new IntersectionObserver(handler);
      observer.observe(header);
    }

    const _getScrollPosition = () => {
      return Math.round(supportPageOffset
        ? window.pageYOffset
        : isCSS1Compat
        ? document.documentElement.scrollTop
        : document.body.scrollTop);
    }

    function _handleNavScroll() {
      const scrolledPosition = _getScrollPosition();

      // reset everything, user has scrolled all the way back to the top
      if (scrolledPosition == 0) {
        header.classList.remove(pinned);
        header.classList.remove(scrollingUp);
      }

      // scrolling down
      if (scrolledPosition > (prevScrollPosition + travelDistance)) {
        header.classList.add(scrollingDown);
        header.classList.remove(scrollingUp);
        prevScrollPosition = scrolledPosition;
      }

      // scrolling up
      if (scrolledPosition < (prevScrollPosition - travelDistance)) {
        header.classList.remove(scrollingDown);
        header.classList.add(scrollingUp);
        prevScrollPosition = scrolledPosition;
      }
    };


    // custom throttle function - taken from https://codepen.io/Jemimaabu/pen/yLVzxdQ
    let throttleTimer:any;

    const _throttle = (callback:any, time:any) => {
      if (throttleTimer) return;

      throttleTimer = true;
      setTimeout(() => {
        callback();
        throttleTimer = false;
      }, time);
    };

    window.addEventListener('scroll', () => {
      _throttle(_handleNavScroll, throttleDuration);
    });
  }

  render(): React.ReactNode {

    const { 
      children, 
      className, 
      innerClassName,
      onMouseLeave,
    } = this.props;

    return (
      <header className={className} ref={ref => this.headerRef = ref} id="io-header" onMouseLeave={onMouseLeave}>
        <div className={innerClassName}>
          { children }
        </div>
      </header>
    )
  }
}

export default StickyHeader;